import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Edit, Trash2, Plus, Filter, Eye, MessageSquare, Download } from 'lucide-react';
import FarmRecordsExport from './FarmRecordsExport';

const FarmRecordsTable = ({ onEdit, onAddNew, onViewDetails }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmer, setSelectedFarmer] = useState('');
  const [selectedFlop, setSelectedFlop] = useState('');
  const [dateFilter, setDateFilter] = useState('currentMonth'); // currentMonth, previousMonth, customRange
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [farmers, setFarmers] = useState([]);
  const [flops, setFlops] = useState([]);
  const [commentModal, setCommentModal] = useState({ show: false, field: '', comment: '', record: null });
  const [showExport, setShowExport] = useState(false);
  const [statistics, setStatistics] = useState({
    total: {},
    average: {},
    minimum: {},
    maximum: {}
  });

  useEffect(() => {
    fetchFarmers();
    fetchFlops();
  }, []);

  useEffect(() => {
    // Only fetch records if a farmer is selected or "All Farmers" is explicitly chosen
    if (selectedFarmer && selectedFarmer !== '') {
      fetchRecords();
    } else {
      // Clear records when no farmer is selected
      setRecords([]);
      setStatistics({
        total: {},
        average: {},
        minimum: {},
        maximum: {}
      });
    }
  }, [selectedFarmer, selectedFlop, dateFilter, customStartDate, customEndDate]);

  const fetchFarmers = async () => {
    try {
      const q = query(collection(db, 'farmers'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    }
  };

  const fetchFlops = async () => {
    try {
      const q = query(collection(db, 'flops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      let q = query(collection(db, 'farmRecords'), orderBy('date', 'desc'));

      // Apply filters if selected
      if (selectedFarmer && selectedFarmer !== 'all') {
        q = query(q, where('farmerId', '==', selectedFarmer));
      }
      if (selectedFlop) {
        q = query(q, where('flopId', '==', selectedFlop));
      }

      const querySnapshot = await getDocs(q);
      let recordsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply date filtering
      recordsData = filterRecordsByDate(recordsData);
      setRecords(recordsData);

      // Calculate statistics
      if (recordsData.length > 0) {
        calculateStatistics(recordsData);
      } else {
        setStatistics({
          total: {},
          average: {},
          minimum: {},
          maximum: {}
        });
      }
    } catch (error) {
      console.error('Error fetching farm records:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (data) => {
    const numericFields = ['tray', 'deliveredTray', 'givenAndCrackTray', 'trayStock', 'newBag', 'perDayBag', 'givenBag', 'bagStock', 'mortality', 'liveBird', 'productionPercentage', 'feedConsumption', 'birdDays'];

    const stats = {
      total: {},
      average: {},
      minimum: {},
      maximum: {}
    };

    numericFields.forEach(field => {
      const values = data.map(record => parseFloat(record[field]) || 0).filter(val => !isNaN(val));

      if (values.length > 0) {
        stats.total[field] = values.reduce((sum, val) => sum + val, 0);
        stats.average[field] = stats.total[field] / values.length;
        stats.minimum[field] = Math.min(...values);
        stats.maximum[field] = Math.max(...values);
      } else {
        stats.total[field] = 0;
        stats.average[field] = 0;
        stats.minimum[field] = 0;
        stats.maximum[field] = 0;
      }
    });

    setStatistics(stats);
  };

  const filterRecordsByDate = (records) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    switch (dateFilter) {
      case 'currentMonth':
        const currentMonthStart = new Date(currentYear, currentMonth, 1);
        const currentMonthEnd = new Date(currentYear, currentMonth + 1, 0);
        return records.filter(record => {
          const recordDate = new Date(record.date);
          return recordDate >= currentMonthStart && recordDate <= currentMonthEnd;
        });

      case 'previousMonth':
        const previousMonthStart = new Date(currentYear, currentMonth - 1, 1);
        const previousMonthEnd = new Date(currentYear, currentMonth, 0);
        return records.filter(record => {
          const recordDate = new Date(record.date);
          return recordDate >= previousMonthStart && recordDate <= previousMonthEnd;
        });

      case 'customRange':
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          const endDate = new Date(customEndDate);
          return records.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
        }
        return records;

      default:
        return records;
    }
  };

  const handleDelete = async (recordId) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await deleteDoc(doc(db, 'farmRecords', recordId));
        fetchRecords();
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Error deleting record. Please try again.');
      }
    }
  };

  const clearFilters = () => {
    setSelectedFarmer('');
    setSelectedFlop('');
    setDateFilter('currentMonth');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const openCommentModal = (field, comment, record) => {
    setCommentModal({
      show: true,
      field: field,
      comment: comment,
      record: record
    });
  };

  const closeCommentModal = () => {
    setCommentModal({ show: false, field: '', comment: '', record: null });
  };

  const handleExport = () => {
    setShowExport(true);
  };

  const closeExport = () => {
    setShowExport(false);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Farm Records</h2>
            <p className="text-sm text-gray-600">Manage farm production and inventory records</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleExport}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </button>
            <button
              onClick={onAddNew}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Record
            </button>
          </div>
        </div>
      </div>

            {/* Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="space-y-4">
          {/* Filter Header */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          
          {/* Filter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Farmer Filter */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700">Farmer *</label>
              <select
                value={selectedFarmer}
                onChange={(e) => setSelectedFarmer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Select Farmer</option>
                <option value="all">All Farmers</option>
                {farmers.map((farmer) => (
                  <option key={farmer.id} value={farmer.id}>{farmer.name}</option>
                ))}
              </select>
            </div>

            {/* Flop Filter */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700">Flop</label>
              <select
                value={selectedFlop}
                onChange={(e) => setSelectedFlop(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All Flops</option>
                {flops.map((flop) => (
                  <option key={flop.id} value={flop.id}>{flop.name}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-700">Date Range</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="currentMonth">This Month</option>
                <option value="previousMonth">Previous Month</option>
                <option value="customRange">Custom Range</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-800 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Custom Date Range - Full Width */}
          {dateFilter === 'customRange' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-200">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Section */}
      {records.length > 0 && (
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Total */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-base sm:text-lg font-semibold text-orange-800 mb-2 sm:mb-3">Total</h4>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <div><span className="font-medium">Tray:</span> {statistics.total.tray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Delivered:</span> {statistics.total.deliveredTray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Mortality:</span> {statistics.total.mortality?.toFixed(0) || 0}</div>
                <div><span className="font-medium">New Bag:</span> {statistics.total.newBag?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Production:</span> {statistics.total.productionPercentage?.toFixed(1) || 0}%</div>
              </div>
            </div>

            {/* Average */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-base sm:text-lg font-semibold text-green-800 mb-2 sm:mb-3">Average</h4>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <div><span className="font-medium">Tray:</span> {statistics.average.tray?.toFixed(1) || 0}</div>
                <div><span className="font-medium">Delivered:</span> {statistics.average.deliveredTray?.toFixed(1) || 0}</div>
                <div><span className="font-medium">Mortality:</span> {statistics.average.mortality?.toFixed(1) || 0}</div>
                <div><span className="font-medium">New Bag:</span> {statistics.average.newBag?.toFixed(1) || 0}</div>
                <div><span className="font-medium">Production:</span> {statistics.average.productionPercentage?.toFixed(1) || 0}%</div>
              </div>
            </div>

            {/* Minimum */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-base sm:text-lg font-semibold text-yellow-800 mb-2 sm:mb-3">Minimum</h4>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <div><span className="font-medium">Tray:</span> {statistics.minimum.tray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Delivered:</span> {statistics.minimum.deliveredTray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Mortality:</span> {statistics.minimum.mortality?.toFixed(0) || 0}</div>
                <div><span className="font-medium">New Bag:</span> {statistics.minimum.newBag?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Production:</span> {statistics.minimum.productionPercentage?.toFixed(1) || 0}%</div>
              </div>
            </div>

            {/* Maximum */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-base sm:text-lg font-semibold text-blue-800 mb-2 sm:mb-3">Maximum</h4>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <div><span className="font-medium">Tray:</span> {statistics.maximum.tray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Delivered:</span> {statistics.maximum.deliveredTray?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Mortality:</span> {statistics.maximum.mortality?.toFixed(0) || 0}</div>
                <div><span className="font-medium">New Bag:</span> {statistics.maximum.newBag?.toFixed(0) || 0}</div>
                <div><span className="font-medium">Production:</span> {statistics.maximum.productionPercentage?.toFixed(1) || 0}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {!selectedFarmer ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-500">
              <h3 className="text-lg font-medium mb-2">Select a Farmer to View Records</h3>
              <p className="text-sm">Choose a farmer from the dropdown above to see their farm records and statistics</p>
            </div>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '2000px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '100px' }}>
                    Farmer
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '120px' }}>
                    Flop
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '60px' }}>
                    Tray
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '90px' }}>
                    Delivered
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '100px' }}>
                    Given & Crack
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Tray Stock
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '70px' }}>
                    New Bag
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Per Day Bag
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Given Bag
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Bag Stock
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '70px' }}>
                    Mortality
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Medicine
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Vaccine
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '90px' }}>
                    Production %
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '100px' }}>
                    Feed Consumption
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Live Bird
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Bird Days
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px' }}>
                    Bird Age
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '100px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.farmerName}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.flopName}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.tray}</span>
                        {record.fieldComments?.tray && (
                          <button
                            onClick={() => openCommentModal('Tray', record.fieldComments.tray, record)}
                            className="group relative"
                            title={record.fieldComments.tray}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.tray}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.deliveredTray}</span>
                        {record.fieldComments?.deliveredTray && (
                          <button
                            onClick={() => openCommentModal('Delivered Tray', record.fieldComments.deliveredTray, record)}
                            className="group relative"
                            title={record.fieldComments.deliveredTray}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.deliveredTray}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.givenAndCrackTray}</span>
                        {record.fieldComments?.givenAndCrackTray && (
                          <button
                            onClick={() => openCommentModal('Given & Crack Tray', record.fieldComments.givenAndCrackTray, record)}
                            className="group relative"
                            title={record.fieldComments.givenAndCrackTray}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.givenAndCrackTray}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {record.trayStock}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.newBag}</span>
                        {record.fieldComments?.newBag && (
                          <button
                            onClick={() => openCommentModal('New Bag', record.fieldComments.newBag, record)}
                            className="group relative"
                            title={record.fieldComments.newBag}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.newBag}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.perDayBag}</span>
                        {record.fieldComments?.perDayBag && (
                          <button
                            onClick={() => openCommentModal('Per Day Bag', record.fieldComments.perDayBag, record)}
                            className="group relative"
                            title={record.fieldComments.perDayBag}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.perDayBag}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.givenBag}</span>
                        {record.fieldComments?.givenBag && (
                          <button
                            onClick={() => openCommentModal('Given Bag', record.fieldComments.givenBag, record)}
                            className="group relative"
                            title={record.fieldComments.givenBag}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.givenBag}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {record.bagStock}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.mortality}</span>
                        {record.fieldComments?.mortality && (
                          <button
                            onClick={() => openCommentModal('Mortality', record.fieldComments.mortality, record)}
                            className="group relative"
                            title={record.fieldComments.mortality}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.mortality}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.medicine}</span>
                        {record.fieldComments?.medicine && (
                          <button
                            onClick={() => openCommentModal('Medicine', record.fieldComments.medicine, record)}
                            className="group relative"
                            title={record.fieldComments.medicine}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.medicine}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <span>{record.vaccine}</span>
                        {record.fieldComments?.vaccine && (
                          <button
                            onClick={() => openCommentModal('Vaccine', record.fieldComments.vaccine, record)}
                            className="group relative"
                            title={record.fieldComments.vaccine}
                          >
                            <MessageSquare className="h-3 w-3 text-orange-600 hover:text-orange-800" />
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              {record.fieldComments.vaccine}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                      {record.productionPercentage}%
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
                      {record.feedConsumption}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {record.liveBird}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.birdDays}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.birdAge || '-'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onViewDetails && onViewDetails(record)}
                          className="text-green-600 hover:text-green-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onEdit(record)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Record"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onEdit(record)}
                          className={`${record.comments ? 'text-orange-600' : 'text-gray-400'} hover:text-orange-900`}
                          title={record.comments ? `Comments: ${record.comments}` : "Add Comments"}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Record"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {records.length === 0 && selectedFarmer && (
              <div className="text-center py-12">
                <p className="text-gray-500">No farm records found for the selected filters.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Comment Modal */}
      {commentModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Comment for {commentModal.field}
              </h3>
              <button
                onClick={closeCommentModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {commentModal.record && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Record:</strong> {commentModal.record.farmerName} - {commentModal.record.flopName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Date:</strong> {new Date(commentModal.record.date).toLocaleDateString()}
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment:
              </label>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-800 whitespace-pre-wrap">
                {commentModal.comment}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={closeCommentModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExport && (
        <FarmRecordsExport onClose={closeExport} />
      )}
    </div>
  );
};

export default FarmRecordsTable; 